import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Check, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ZOOM_SCHEDULER = 'https://scheduler.zoom.us/adam-henry-8vgigm/45-mins-with-adam';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FULL_PRICE = '$22,000';
const DISCOUNT_PRICE = '$11,000';

const QUICK_TIER = {
  name: "Don't Waste My FUCK'N Time",
  nameSuffix: 'Consultation',
  price: 'Complimentary',
  frequency: '5-minute call',
  description: 'Have a couple quick questions? Book a brief call with Adam to get clarity on specific topics.',
  features: [
    'Direct 5-minute call with Adam',
    'Ask up to 2 questions',
    'Quick clarity on specific topics',
    'No obligation, no pressure',
  ],
  cta: 'Book Quick Call',
  testId: 'pricing-tier-quick',
  featured: false,
};

const SETUP_FEATURES = [
  "One-on-one Q&A conversation with TrepTradingTech's Founder, Adam",
  'Analyze your current trading approach, if any',
  'Review your current financials, including income goals and retirement plans',
  'Strategize a plan of execution to deploy algorithms tailored to your goals',
  'Done-for-you automated portfolio setup & personal brokerage account linking',
  'Discuss how much capital needed to meet goals and start application process for funding',
  'Guaranteed approval for a minimum of $450,000 in trading firm capital for program members',
  'Access to custom mobile app and entry into Mentorship',
];

function formatCountdown(totalSeconds) {
  if (totalSeconds <= 0) return { days: 0, hours: 0, minutes: 0, secs: 0 };
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);
  return { days, hours, minutes, secs };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

export const PricingSection = () => {
  const [withinWindow, setWithinWindow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Step 1: Record this IP's first pricing unlock (no-op if already recorded)
    fetch(`${BACKEND}/api/track/pricing-unlock`, { method: 'POST' }).catch(() => {});

    // Step 2: Fetch countdown data
    fetch(`${BACKEND}/api/pricing/timer`)
      .then(r => r.json())
      .then(data => {
        if (data.within_window && data.seconds_remaining > 0) {
          setWithinWindow(true);
          setSecondsLeft(data.seconds_remaining);
        }
      })
      .catch(() => {});
  }, []);

  // Live countdown tick
  useEffect(() => {
    if (!withinWindow || secondsLeft <= 0) return;

    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setWithinWindow(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [withinWindow]);

  const { days, hours, minutes, secs } = formatCountdown(secondsLeft);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32"
      style={{ backgroundColor: '#000000' }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div
            className="inline-flex items-center gap-2 mb-6"
            style={{ fontFamily: 'DM Sans', color: '#6B7280', letterSpacing: '2px', textTransform: 'uppercase', fontSize: '11px', fontWeight: 500 }}
          >
            Next Steps
          </div>
          <h2
            className="text-5xl font-bold mb-0"
            style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF', letterSpacing: '-0.02em' }}
          >
            Two Ways to Connect
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" style={{ alignItems: 'stretch' }}>

          {/* ─── Tier 1: Quick Consultation ─── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0 }}
            data-testid="pricing-tier-quick"
            className="relative flex flex-col p-10"
            style={{
              backgroundColor: 'rgba(10,10,10,1)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div className="mb-8 text-center">
              <div
                className="font-bold leading-tight flex flex-col items-center justify-end"
                style={{
                  fontFamily: 'DM Sans',
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  fontSize: '15px',
                  minHeight: '100px',
                  marginBottom: '20px',
                }}
              >
                <span style={{ color: '#16a34a', textDecoration: 'underline', textDecorationColor: '#16a34a', textUnderlineOffset: '4px' }}>
                  {QUICK_TIER.name}
                </span>
                <span
                  style={{
                    background: 'linear-gradient(90deg, #2563EB 0%, #60A5FA 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'block',
                  }}
                >
                  {QUICK_TIER.nameSuffix}
                </span>
              </div>

              <div className="mb-1">
                <span
                  className="font-bold"
                  style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF', fontSize: '34px' }}
                >
                  {QUICK_TIER.price}
                </span>
              </div>
              <div className="text-sm mb-2" style={{ fontFamily: 'DM Sans', color: '#6B7280' }}>{QUICK_TIER.frequency}</div>
              <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}>{QUICK_TIER.description}</p>
            </div>

            <div className="h-px mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

            <div className="space-y-4 flex-1 mb-8">
              {QUICK_TIER.features.map((f, j) => (
                <div key={j} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 mt-2 flex-shrink-0" style={{ backgroundColor: '#6B7280' }} />
                  <span className="text-sm leading-relaxed" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>{f}</span>
                </div>
              ))}
            </div>

            <a href={ZOOM_SCHEDULER} target="_blank" rel="noopener noreferrer" className="block">
              <Button
                data-testid="pricing-tier-cta-button"
                className="w-full h-12 text-sm font-semibold inline-flex items-center justify-center gap-2"
                style={{
                  background: 'transparent',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255,255,255,0.15)',
                  fontFamily: 'DM Sans',
                  letterSpacing: '0.5px',
                }}
              >
                {QUICK_TIER.cta}
                <ExternalLink size={14} />
              </Button>
            </a>
          </motion.div>

          {/* ─── Tier 2: Setup Consultation ─── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="pricing-tier-setup"
            className="relative flex flex-col p-10"
            style={{
              backgroundColor: 'rgba(30,64,175,0.03)',
              border: '1px solid rgba(30,64,175,0.3)',
            }}
          >
            {/* Recommended badge */}
            <div
              className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1.5 text-xs font-semibold"
              style={{ backgroundColor: '#1E40AF', color: '#FFFFFF', fontFamily: 'DM Sans', letterSpacing: '1px' }}
            >
              RECOMMENDED
            </div>

            <div className="mb-8 text-center">
              <div
                className="font-bold leading-tight flex flex-col items-center justify-end"
                style={{
                  fontFamily: 'DM Sans',
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  fontSize: '15px',
                  minHeight: '100px',
                  marginBottom: '20px',
                }}
              >
                <span style={{ color: '#16a34a', textDecoration: 'underline', textDecorationColor: '#16a34a', textUnderlineOffset: '4px' }}>
                  Call me or go teach 3rd grade public school
                </span>
                <span
                  style={{
                    background: 'linear-gradient(90deg, #2563EB 0%, #60A5FA 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'block',
                  }}
                >
                  Consultation
                </span>
              </div>

              {/* ─── Price block: discount vs standard ─── */}
              {withinWindow ? (
                <div className="mb-2" data-testid="discount-price-block">
                  {/* Strikethrough original price */}
                  <div className="flex items-center justify-center gap-3 mb-1">
                    <div className="relative inline-block">
                      <span
                        style={{
                          fontFamily: 'Cormorant Garamond',
                          color: '#6B7280',
                          fontSize: '28px',
                          fontWeight: 600,
                        }}
                      >
                        {FULL_PRICE}
                      </span>
                      {/* Red diagonal strikethrough */}
                      <svg
                        viewBox="0 0 100 30"
                        preserveAspectRatio="none"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '-4%',
                          width: '108%',
                          height: '100%',
                          pointerEvents: 'none',
                        }}
                      >
                        <line x1="0" y1="80%" x2="100" y2="20%" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>

                  {/* Discounted price */}
                  <div className="flex items-baseline justify-center gap-2">
                    <span
                      style={{
                        fontFamily: 'Cormorant Garamond',
                        color: '#FFFFFF',
                        fontSize: '54px',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {DISCOUNT_PRICE}
                    </span>
                  </div>

                  {/* Savings badge */}
                  <div
                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1"
                    style={{
                      backgroundColor: 'rgba(239,68,68,0.12)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '2px',
                    }}
                  >
                    <span style={{ color: '#FCA5A5', fontSize: '11px', fontFamily: 'DM Sans', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600 }}>
                      Save $11,000 — Limited Time
                    </span>
                  </div>

                  {/* Countdown timer */}
                  <div
                    className="mt-4 p-2"
                    data-testid="countdown-timer"
                    style={{
                      backgroundColor: 'rgba(239,68,68,0.06)',
                      border: '1px solid rgba(239,68,68,0.2)',
                    }}
                  >
                    <div
                      className="flex items-center justify-center gap-1 mb-1.5"
                      style={{ color: '#FCA5A5', fontSize: '8px', fontFamily: 'DM Sans', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      <Clock size={8} />
                      Offer Expires In
                    </div>
                    <div className="flex items-end justify-center gap-2">
                      {[
                        { val: days, label: 'Days' },
                        { val: hours, label: 'Hrs' },
                        { val: minutes, label: 'Min' },
                        { val: secs, label: 'Sec' },
                      ].map(({ val, label }, idx) => (
                        <React.Fragment key={label}>
                          {idx > 0 && (
                            <span style={{ color: '#EF4444', fontSize: '15px', fontFamily: 'Azeret Mono', lineHeight: 1, paddingBottom: '9px' }}>:</span>
                          )}
                          <div className="flex flex-col items-center">
                            <span
                              style={{
                                fontFamily: 'Azeret Mono',
                                fontSize: '20px',
                                fontWeight: 700,
                                color: '#EF4444',
                                lineHeight: 1,
                                minWidth: '26px',
                                textAlign: 'center',
                              }}
                            >
                              {pad(val)}
                            </span>
                            <span style={{ color: '#9CA3AF', fontSize: '7px', fontFamily: 'DM Sans', letterSpacing: '1px', textTransform: 'uppercase', marginTop: '3px' }}>
                              {label}
                            </span>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-1" data-testid="standard-price-block">
                  <span
                    className="font-bold"
                    style={{ fontFamily: 'Cormorant Garamond', color: '#FFFFFF', fontSize: '48px' }}
                  >
                    {FULL_PRICE}
                  </span>
                </div>
              )}

              <div className="text-sm mb-2 mt-2" style={{ fontFamily: 'DM Sans', color: '#6B7280' }}>55-minute call</div>

              <div
                className="text-xs font-semibold mb-5 tracking-wide"
                style={{
                  fontFamily: 'DM Sans',
                  background: 'linear-gradient(90deg, #2563EB, #60A5FA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                }}
              >
                From Consultation to Implementation
              </div>
            </div>

            <div className="h-px mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

            <div className="space-y-4 flex-1 mb-8">
              {SETUP_FEATURES.map((f, j) => (
                <div key={j} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 mt-2 flex-shrink-0" style={{ backgroundColor: '#1E40AF' }} />
                  <span className="text-sm leading-relaxed" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>{f}</span>
                </div>
              ))}
            </div>

            <a href={ZOOM_SCHEDULER} target="_blank" rel="noopener noreferrer" className="block">
              <Button
                data-testid="pricing-tier-cta-button"
                className="w-full h-12 text-sm font-semibold inline-flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 60%, #1e3a8a 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  fontFamily: 'DM Sans',
                  letterSpacing: '0.5px',
                  boxShadow: '0 8px 32px rgba(30, 64, 175, 0.35)',
                }}
              >
                Book Setup Call
                <ExternalLink size={14} />
              </Button>
            </a>
          </motion.div>

        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-sm text-center mt-12 max-w-2xl mx-auto leading-relaxed"
          style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}
        >
          Both options use the same booking scheduler. When you get to the booking page, please mention the name of your consultation package in the booking notes.
        </motion.p>
      </div>
    </section>
  );
};
